import React, { useEffect, useState } from 'react';

import { Box, Button, TextField } from '@mui/material';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userReducer } from '../../store/reducers/user.reducer';
import { getInvitationSuccess, getIsSendingInvitation, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';

const { sendInvitation } = userReducer.actions;

export const InvitationPage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const isSendingInvitation = useAppSelector(getIsSendingInvitation);
  const invitationSuccess = useAppSelector(getInvitationSuccess);
  const [email, setEmail] = useState('');
  const [invitationExpiryHours, setInvitationExpiryHours] = useState(168);
  const [initialCredits, setInitialCredits] = useState(2000);
  const [emailError, setEmailError] = useState('');
  const [expiryError, setExpiryError] = useState('');
  const [creditsError, setCreditsError] = useState('');

  // Check if user is admin, redirect to forbidden if not
  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  // Reset form when invitation is sent successfully
  useEffect(() => {
    if (invitationSuccess) {
      setEmail('');
      setInvitationExpiryHours(168);
      setInitialCredits(2000);
    }
  }, [invitationSuccess]);

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError('Please provide an email');
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setEmailError('');
    return true;
  };

  const validateExpiryHours = (hours: number) => {
    if (!hours || hours <= 0) {
      setExpiryError('Expiry hours must be greater than 0');
      return false;
    }

    if (hours > 8760) {
      // 1 year in hours
      setExpiryError('Expiry hours cannot exceed 8760 (1 year)');
      return false;
    }

    setExpiryError('');
    return true;
  };

  const validateCredits = (credits: number) => {
    if (!credits || credits < 0) {
      setCreditsError('Initial credits must be 0 or greater');
      return false;
    }

    setCreditsError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isEmailValid = validateEmail(email);
    const isExpiryValid = validateExpiryHours(invitationExpiryHours);
    const isCreditsValid = validateCredits(initialCredits);

    if (!isEmailValid || !isExpiryValid || !isCreditsValid) {
      return;
    }

    dispatch(
      sendInvitation({
        email,
        invitationExpiryHours,
        initialCredits,
      }),
    );
  };

  // Don't render anything if user is not admin (will redirect)
  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  return (
    <Box>
      <AdminPageHeader
        title="Send Invitation"
        subtitle="Send an invitation to a new user with specified credits and expiry time."
      />
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="User Email"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          disabled={isSendingInvitation}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          id="invitationExpiryHours"
          label="Invitation Expiry Hours"
          name="invitationExpiryHours"
          type="number"
          value={invitationExpiryHours}
          onChange={(e) => setInvitationExpiryHours(Number(e.target.value))}
          error={!!expiryError}
          helperText={expiryError || 'Default: 168 hours'}
          disabled={isSendingInvitation}
          inputProps={{ min: 1, max: 8760 }}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          id="initialCredits"
          label="Initial Credits"
          name="initialCredits"
          type="number"
          value={initialCredits}
          onChange={(e) => setInitialCredits(Number(e.target.value))}
          error={!!creditsError}
          helperText={creditsError || 'Default: 20000 credits'}
          disabled={isSendingInvitation}
          inputProps={{ min: 0 }}
        />

        <Button type="submit" variant="contained" sx={{ mt: 3 }} disabled={isSendingInvitation}>
          {isSendingInvitation ? 'Sending Invitation...' : 'Send Invitation'}
        </Button>
      </Box>
    </Box>
  );
};
